// src/utils/ViewReport/viewReportFields.js

/**
 * Canonical field catalogue for view-report data capture.
 * Each field definition describes what the value represents, the preferred data type,
 * and the default value to surface before the user provides input.
 * 
 * This follows the same pattern as onboardingFields.js for consistency.
 */
export const VIEW_REPORT_FIELD_DEFINITIONS = Object.freeze({
  // =====================================
  // Session & Tracking Links
  // =====================================
  sessionIds: {
    key: 'sessionIds',
    label: 'Linked Tracking Session IDs',
    description: 'One or more tracking session IDs associated with this view-report session.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  
  // =====================================
  // Report Context
  // =====================================
  reportRequestId: {
    key: 'reportRequestId',
    label: 'Report Request ID',
    description: 'The requestId from the URL that identifies the report being viewed.',
    valueType: 'string',
    initialValue: null,
  },
  linkedOnboardingId: {
    key: 'linkedOnboardingId',
    label: 'Linked Onboarding ID',
    description: 'The onboarding session ID that created this report (if available from metadata).',
    valueType: 'string',
    initialValue: null,
  },
  reportPassword: {
    key: 'reportPassword',
    label: 'Report Password Used',
    description: 'The password hash used to access the report (stored for validation tracking only).',
    valueType: 'string',
    initialValue: null,
  },
  
  // =====================================
  // Landing & Navigation
  // =====================================
  landingUrl: {
    key: 'landingUrl',
    label: 'Landing URL',
    description: 'Full URL where the user landed on the view-report page.',
    valueType: 'string',
    initialValue: null,
  },
  referrer: {
    key: 'referrer',
    label: 'Referrer URL',
    description: 'The document.referrer when the user arrived.',
    valueType: 'string',
    initialValue: null,
  },
  utmParameters: {
    key: 'utmParameters',
    label: 'UTM Parameters',
    description: 'UTM tracking parameters extracted from the landing URL.',
    valueType: 'object',
    initialValue: null,
  },
  tabsViewed: {
    key: 'tabsViewed',
    label: 'Tabs Viewed',
    description: 'Array of tabs the user viewed (analysis, generation).',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  
  // =====================================
  // API Calls & Function Returns
  // =====================================
  fetchReportAttempts: {
    key: 'fetchReportAttempts',
    label: 'Fetch Report Attempts',
    description: 'Array of API call attempts to fetch report data. Each entry contains timestamp, reportType, success, and error details.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  reportMetadataReceived: {
    key: 'reportMetadataReceived',
    label: 'Report Metadata Received',
    description: 'Metadata returned from the report API (userName, candidateScorePercent, etc.).',
    valueType: 'object',
    initialValue: null,
  },
  
  // =====================================
  // Contact Information (Phone)
  // =====================================
  primaryContactPhoneNumber: {
    key: 'primaryContactPhoneNumber',
    label: 'Primary Contact Phone Number',
    description: 'Full international phone number string the user provided for follow up.',
    valueType: 'string',
    initialValue: null,
  },
  primaryContactDialCode: {
    key: 'primaryContactDialCode',
    label: 'Primary Contact Dial Code',
    description: 'E.164 dial prefix captured from the selected country.',
    valueType: 'string',
    initialValue: null,
  },
  primaryContactPhoneRegion: {
    key: 'primaryContactPhoneRegion',
    label: 'Primary Contact Phone Region',
    description: 'ISO country code associated with the chosen phone number.',
    valueType: 'string',
    initialValue: null,
  },
  
  // =====================================
  // Contact Information (Email)
  // =====================================
  primaryContactEmailAddress: {
    key: 'primaryContactEmailAddress',
    label: 'Primary Contact Email Address',
    description: 'Email address provided by the user for follow up.',
    valueType: 'string',
    initialValue: null,
  },
  preferredFollowUpChannel: {
    key: 'preferredFollowUpChannel',
    label: 'Preferred Follow-up Channel',
    description: 'The communication channel the user prefers for follow up (e.g., phone, email).',
    valueType: 'string',
    initialValue: null,
  },
  
  // =====================================
  // Contact Form Submissions (Append-Only)
  // =====================================
  contactSubmissions: {
    key: 'contactSubmissions',
    label: 'Contact Submissions',
    description: 'Array of all contact form submissions. Each entry contains timestamp, type (phone/email), value, and submission source.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  welcomeEmailAttempts: {
    key: 'welcomeEmailAttempts',
    label: 'Welcome Email Attempts',
    description: 'Array of welcome email send attempts. Each entry contains email, timestamp, success, and error details.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  
  // =====================================
  // Contact Button Clicks (Append-Only)
  // =====================================
  contactButtonClicks: {
    key: 'contactButtonClicks',
    label: 'Contact Button Clicks',
    description: 'Array of contact button click events (WhatsApp, Messenger, iMessage, Email). Each entry contains pageName, buttonType, href, timestamp.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  
  // =====================================
  // User Navigation Log (Append-Only)
  // =====================================
  userNavLog: {
    key: 'userNavLog',
    label: 'User Navigation Log',
    description: 'Chronological array of page/view names representing the user\'s navigation path through view-report.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  pageViewDurations: {
    key: 'pageViewDurations',
    label: 'Page View Durations',
    description: 'Array of page view duration records. Each entry contains pageName, enterTimestamp, leaveTimestamp, durationMs.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
  
  // =====================================
  // Error Tracking (Append-Only)
  // =====================================
  errors: {
    key: 'errors',
    label: 'Errors',
    description: 'Array of errors encountered during the view-report session. Each entry contains timestamp, type, message, context.',
    valueType: 'array',
    allowsMultipleEntries: true,
    initialValue: [],
  },
});

export const VIEW_REPORT_FIELD_KEYS = Object.freeze(Object.keys(VIEW_REPORT_FIELD_DEFINITIONS));

export function getViewReportFieldDefinition(fieldKey) {
  return VIEW_REPORT_FIELD_DEFINITIONS[fieldKey] || null;
}

export function createEmptyViewReportFieldState(fieldKey) {
  const definition = getViewReportFieldDefinition(fieldKey);
  if (!definition) {
    throw new Error(`Unknown view-report field: ${fieldKey}`);
  }

  return {
    key: fieldKey,
    definition,
    history: [],
    latestValue: definition.initialValue ?? null,
    latestUpdatedAt: null,
    lastValueSource: null,
    firstFilledAt: null,
    fillCount: 0,
    status: 'never_filled',
  };
}

export function createInitialViewReportFieldsState() {
  const fields = {};
  for (const key of VIEW_REPORT_FIELD_KEYS) {
    fields[key] = createEmptyViewReportFieldState(key);
  }
  return fields;
}
